import assert from 'assert';
import { captureException } from '@sentry/nextjs';
import { parseEther } from 'viem';
import type { MintSchedule, V2_1MintSchedule, V2MintSchedule } from '@soundxyz/sdk';
import { minterAbiMap } from '@soundxyz/sdk/utils/constants';
import type { Chains } from '@soundxyz/utils/constants/constants';
import {
  getContractsForChain,
  getDeprecateMintersForChain,
} from '@soundxyz/utils/constants/contract-addresses';
import { FREE_SALE_SCHEDULE_MAX_MINTS_PER_WALLET } from '@components/Dashboard/Upload/helpers';
import { AuctionType } from '@graphql/reactQuery';
import { SoundContractInterfaceIds } from '@utils/constants';

export const MULTI_CALLER_WITH_SENDER_MINTER_CONTRACT_ADDRESS =
  '0x00000000002Fd5Aeb385D324B580FCa7c83823A0';

export function isV2CompatibleMintSchedule(
  schedule: MintSchedule,
): schedule is V2MintSchedule | V2_1MintSchedule {
  return [
    SoundContractInterfaceIds.IMerkleDropMinterV2,
    SoundContractInterfaceIds.IRangeEditionMinterV2,
    SoundContractInterfaceIds.IMerkleDropMinterV2_1,
    SoundContractInterfaceIds.IRangeEditionMinterV2_1,
  ].includes(schedule.interfaceId);
}

export const SAMBasePriceFallBack = '0.001';

/**
 * In admin tool, we set a multiple of the public mint price to be the inflection price.
 *
 * Basically the curve slopes up to the inflection price at the inflection count, then
 * it become a square root curve (sigmoid = quadratic + square root).
 */
export function computeInflectionPrice({
  basePrice,
  inflectionMultipleBPS,
}: {
  basePrice: bigint;
  inflectionMultipleBPS: number;
}): bigint {
  let updatedBasePrice: bigint = basePrice;
  if (basePrice === 0n) {
    updatedBasePrice = BigInt(parseEther(SAMBasePriceFallBack).toString());
  }
  const inflectionPrice = (updatedBasePrice * BigInt(inflectionMultipleBPS)) / 10_000n;
  assert(inflectionPrice > 0n, 'Inflection price needs to be greater than 0');
  return inflectionPrice;
}

// only add update maxMintable if
// 1. fixed auction type
// 2. maxMintable is different
// 3. total minted is 0
export function canUpdateMaxMintable({
  maxMintableToUpdate,
  contractMaxMintable,
  totalMinted,
  auctionType,
}: {
  maxMintableToUpdate: number;
  contractMaxMintable: number;
  totalMinted: bigint;
  auctionType: AuctionType;
}): boolean {
  return (
    auctionType === AuctionType.FixedQuantity &&
    maxMintableToUpdate !== contractMaxMintable &&
    totalMinted === 0n
  );
}

// if price is 0, maxMintsPerWallet always needs to be 1
export function isValidMaxMintablePerAccount({
  saleSchedulePrice,
  maxMintablePerAccount,
}: {
  saleSchedulePrice: bigint;
  maxMintablePerAccount: number;
}): boolean {
  return (
    saleSchedulePrice > 0n ||
    (saleSchedulePrice === 0n && maxMintablePerAccount === FREE_SALE_SCHEDULE_MAX_MINTS_PER_WALLET)
  );
}

export function getMinterByAddress({
  minterContractAddress,
  chain,
}: {
  minterContractAddress: string;
  chain: Chains;
}) {
  const deprecatedMinters = getDeprecateMintersForChain(chain);
  const currentMinters = getContractsForChain(chain);

  switch (minterContractAddress.toLowerCase()) {
    // only goerli/mainnet has v1 deprecated minters
    // TODO: clean up typing here so we aren't comparing w/ undefined
    case deprecatedMinters.v1?.rangeEditionMinter.toLowerCase(): {
      return {
        minterType: 'RangeEdition' as const,
        abi: minterAbiMap[SoundContractInterfaceIds.IRangeEditionMinter],
      };
    }
    case deprecatedMinters.v1?.merkleDropMinter.toLowerCase(): {
      return {
        minterType: 'MerkleDrop' as const,
        abi: minterAbiMap[SoundContractInterfaceIds.IMerkleDropMinter],
      };
    }

    case deprecatedMinters.v2.rangeEditionMinterV2.toLowerCase():
    case currentMinters.rangeEditionMinterV2_1.toLowerCase(): {
      return {
        minterType: 'RangeEditionV2' as const,
        abi: minterAbiMap[SoundContractInterfaceIds.IRangeEditionMinterV2],
      };
    }

    case deprecatedMinters.v2.merkleDropMinterV2.toLowerCase():
    case currentMinters.merkleDropMinterV2_1.toLowerCase(): {
      return {
        minterType: 'MerkleDropMinterV2' as const,
        abi: minterAbiMap[SoundContractInterfaceIds.IMerkleDropMinterV2],
      };
    }

    default: {
      const err = new Error(
        `Minter contract address ${minterContractAddress} does not match any known minter factory`,
      );
      captureException(err, {
        level: 'error',
        tags: {
          minterContractAddress,
        },
      });
      throw err;
    }
  }
}
